import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import UploadProgressItem from './UploadProgressItem';
import type { UploadFile } from '@hooks/useDocumentUpload';

interface UploadProgressTrackerProps {
  uploadFiles: UploadFile[];
  onRetry: (fileId: string) => void;
}

const UploadProgressTracker: React.FC<UploadProgressTrackerProps> = ({
  uploadFiles,
  onRetry,
}) => {
  const { t } = useTranslation();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleExpanded = (fileId: string) =>
    setExpandedItems((current) => {
      const next = new Set(current);
      if (!next.delete(fileId)) next.add(fileId);
      return next;
    });

  const completedCount = uploadFiles.filter((f) => f.status === 'completed').length;
  const errorCount = uploadFiles.filter((f) => f.status === 'error').length;
  const totalCount = uploadFiles.length;
  const overallProgress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="revy-cjk text-sm font-medium leading-[1.5]">
            {t('upload.progressTracker.overallProgress')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 pt-0">
          <div className="flex items-center gap-3">
            <Progress value={overallProgress} className="h-2 flex-1" />
            <span className="revy-figure text-xs leading-[1.5] text-muted-foreground">
              {completedCount}/{totalCount}
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            <Badge
              variant="outline"
              className="revy-cjk revy-figure border-success/40 px-2 py-0 text-[11px] font-medium leading-[1.5] text-success"
            >
              {t('upload.progressTracker.completedCount', { count: completedCount })}
            </Badge>
            {errorCount > 0 && (
              <Badge
                variant="outline"
                className="revy-cjk revy-figure border-destructive/40 px-2 py-0 text-[11px] font-medium leading-[1.5] text-destructive"
              >
                {t('upload.progressTracker.failedCount', { count: errorCount })}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="revy-cjk text-sm font-medium leading-[1.5]">
            {t('upload.progressTracker.fileProgress')}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <ul className="space-y-2">
            {uploadFiles.map((uploadFile) => (
              <UploadProgressItem
                key={uploadFile.id}
                uploadFile={uploadFile}
                isExpanded={expandedItems.has(uploadFile.id)}
                onToggleExpanded={() => toggleExpanded(uploadFile.id)}
                onRetry={() => onRetry(uploadFile.id)}
              />
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default UploadProgressTracker;
